
import React, { useState, useEffect } from 'react';
import { Annotation, GeneratedImage } from '../types';
import { Tag, Trash2, Type, Download, FileText, Image as ImageIcon, Loader2, Undo, Redo, MousePointer2 } from 'lucide-react';
import pptxgen from 'pptxgenjs';

interface EditorPanelProps {
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  generatedPrompt: string;
  generatedImage: GeneratedImage | null;
  onAnnotationFocus: (id: string | null) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

// Sub-component to manage local input state and prevent history spam
const AnnotationItem: React.FC<{
  annotation: Annotation;
  index: number;
  onUpdate: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onFocus: (id: string | null) => void;
}> = ({ annotation, index, onUpdate, onDelete, onFocus }) => {
  const [localText, setLocalText] = useState(annotation.text);

  // Sync local state if parent updates (e.g. undo/redo)
  useEffect(() => {
    setLocalText(annotation.text);
  }, [annotation.text]);

  const handleBlur = () => {
    if (localText !== annotation.text) {
      onUpdate(annotation.id, localText);
    }
    onFocus(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <div className="bg-slate-800 p-3 rounded border border-slate-700 shadow-sm animate-in fade-in slide-in-from-left-4 duration-300 transition-colors hover:border-slate-600 group">
      <div className="flex items-center gap-2 mb-2">
        <span className="bg-cyan-900/50 text-cyan-400 text-[10px] font-mono px-1.5 py-0.5 rounded group-hover:bg-cyan-900/70 transition-colors">#{index + 1}</span>
        <span className="text-xs text-slate-400">Position: {Math.round(annotation.targetX)}%, {Math.round(annotation.targetY)}%</span>
        <button 
          onClick={() => onDelete(annotation.id)}
          className="ml-auto text-slate-500 hover:text-red-400 transition-colors p-1 hover:bg-slate-700 rounded opacity-0 group-hover:opacity-100 focus:opacity-100"
          title="Delete Label"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-2 bg-slate-900 p-1.5 rounded border border-slate-700 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500/50 transition-all">
        <Type className="w-3.5 h-3.5 text-slate-500 ml-1 flex-shrink-0" />
        <input 
          type="text" 
          value={localText}
          onChange={(e) => setLocalText(e.target.value)}
          onFocus={() => onFocus(annotation.id)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter label..."
          className="bg-transparent w-full text-sm text-white focus:outline-none placeholder:text-slate-600"
        />
      </div>
    </div>
  );
};

const EditorPanel: React.FC<EditorPanelProps> = ({ 
  annotations, 
  setAnnotations, 
  generatedPrompt, 
  generatedImage, 
  onAnnotationFocus,
  onUndo, 
  onRedo,
  canUndo,
  canRedo
}) => {
  const [isExporting, setIsExporting] = useState(false);

  // Keyboard Shortcuts for Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); // Prevent browser undo
        if (e.shiftKey) {
          if (canRedo) onRedo();
        } else {
          if (canUndo) onUndo();
        }
      }
      // Common alternative for Redo: Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        if (canRedo) onRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onUndo, onRedo, canUndo, canRedo]);
  
  const updateAnnotationText = (id: string, newText: string) => {
    setAnnotations(prev => prev.map(a => a.id === id ? { ...a, text: newText } : a));
  };

  const deleteAnnotation = (id: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== id));
  };

  const handleExportPPTX = async () => {
    if (!generatedImage) return;
    if (!window.confirm('Are you sure you want to export this image with annotations?')) return;
    
    setIsExporting(true);

    try {
      const pres = new pptxgen();
      const slide = pres.addSlide();

      // Add Image (Centered, max size)
      slide.addImage({ 
        data: generatedImage.url, 
        x: 0.5, 
        y: 0.5, 
        w: '90%', 
        h: '90%',
        sizing: { type: 'contain', w: 9, h: 5 } // specific container size
      });

      // Add Annotations
      // Note: PPTX coords are in inches by default. Slide is 10x5.625 usually.
      // We need to map 0-100% to the image bounding box on the slide.
      // For simplicity, we assume the image takes up most of the slide.
      
      // Let's assume a 10 inch width slide for calculation approximation
      // In a real app, we'd calculate exact image aspect ratio placement.
      
      annotations.forEach(ann => {
        // Approximate mapping to slide coordinates (assuming full slide image for simplicity in this prototype)
        // 10 inches wide, 5.625 inches high
        const slideW = 10;
        const slideH = 5.625;
        
        const targetX = (ann.targetX / 100) * slideW;
        const targetY = (ann.targetY / 100) * slideH;
        const labelX = (ann.labelX / 100) * slideW;
        const labelY = (ann.labelY / 100) * slideH;

        // Draw Line
        slide.addShape(pres.ShapeType.line, {
          x: labelX,
          y: labelY,
          w: targetX - labelX,
          h: targetY - labelY,
          line: { color: '06B6D4', width: 2 }
        });

        // Draw Label Box
        slide.addText(ann.text, {
          x: labelX - 0.75, // Center the box approx
          y: labelY - 0.2,
          w: 1.5,
          h: 0.4,
          fill: { color: 'FFFFFF', transparency: 10 },
          color: '0F172A',
          fontSize: 10,
          bold: true,
          align: 'center'
        });
      });

      // Add Disclaimer
      slide.addText("AI Generated - Educational Use Only", {
        x: 0,
        y: 5.3,
        w: '100%',
        h: 0.3,
        fontSize: 8,
        color: '94A3B8',
        align: 'center'
      });

      await pres.writeFile({ fileName: `RJ-MedGraph-Export-${Date.now()}.pptx` });
    } catch (e) {
      console.error("PPTX Export Error", e);
      alert("Failed to export PowerPoint.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportHighRes = async () => {
    if (!generatedImage) return;
    if (!window.confirm('Are you sure you want to export this image with annotations?')) return;
    
    setIsExporting(true);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.src = generatedImage.url;
      
      await new Promise((resolve) => {
        img.onload = () => resolve(true);
      });

      // Set canvas to high res (e.g., 300 DPI equivalent logic -> scale up)
      // Standard is 72 DPI. 300 DPI is ~4.16x
      // Let's just do 3x dimension for safety/performance
      const scale = 3;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      // Draw Image
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);

      // Draw Annotations
      annotations.forEach(ann => {
        const targetX = (ann.targetX / 100) * img.width;
        const targetY = (ann.targetY / 100) * img.height;
        const labelX = (ann.labelX / 100) * img.width;
        const labelY = (ann.labelY / 100) * img.height;

        // Line
        ctx.beginPath();
        ctx.moveTo(labelX, labelY);
        ctx.lineTo(targetX, targetY);
        ctx.strokeStyle = '#06b6d4'; // Cyan-500
        ctx.lineWidth = 3;
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 4;
        ctx.stroke();

        // Arrowhead
        const headLen = 10;
        const angle = Math.atan2(targetY - labelY, targetX - labelX);
        ctx.beginPath();
        ctx.moveTo(targetX, targetY);
        ctx.lineTo(targetX - headLen * Math.cos(angle - Math.PI / 6), targetY - headLen * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(targetX - headLen * Math.cos(angle + Math.PI / 6), targetY - headLen * Math.sin(angle + Math.PI / 6));
        ctx.fillStyle = '#06b6d4';
        ctx.fill();

        // Label Background
        ctx.font = 'bold 24px Inter, sans-serif';
        const textMetrics = ctx.measureText(ann.text);
        const pad = 12;
        const boxW = textMetrics.width + (pad * 2);
        const boxH = 40;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 6;
        // Draw rounded rect centered on labelX/Y
        const r = 6;
        const x = labelX - (boxW / 2);
        const y = labelY - (boxH / 2);
        
        ctx.beginPath();
        ctx.roundRect(x, y, boxW, boxH, r);
        ctx.fill();

        // Label Text
        ctx.fillStyle = '#0f172a';
        ctx.shadowColor = 'transparent';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(ann.text, labelX, labelY);
      });

      // Try TIFF or fallback
      const mimeType = 'image/tiff'; 
      let dataUrl = canvas.toDataURL(mimeType, 1.0);
      let ext = 'tiff';
      
      // Check if browser supported it
      if (dataUrl.startsWith('data:image/png')) {
        ext = 'png'; // Fallback
      }

      const link = document.createElement('a');
      link.download = `RJ-MedGraph-Export-${Date.now()}.${ext}`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (e) {
      console.error("Export Error", e);
      alert("Failed to export image.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="w-72 bg-slate-900 border-l border-slate-700 flex flex-col h-full flex-shrink-0 hidden md:flex z-10">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex justify-between items-center">
         <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
           <Tag className="w-4 h-4 text-cyan-500" />
           Labeling Layer
         </h3>
         <div className="flex gap-1">
             <button 
               onClick={onUndo} 
               disabled={!canUndo} 
               className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
               title="Undo (Ctrl+Z)"
             >
               <Undo className="w-4 h-4" />
             </button>
             <button 
               onClick={onRedo} 
               disabled={!canRedo} 
               className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
               title="Redo (Ctrl+Shift+Z)"
             >
               <Redo className="w-4 h-4" />
             </button>
         </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        
        {/* Export Actions */}
        <div className="bg-slate-800 p-3 rounded-lg border border-slate-700 space-y-2">
           <div className="text-xs text-slate-400 font-semibold uppercase mb-2">Export Options</div>
           
           <button 
             onClick={handleExportPPTX}
             disabled={!generatedImage || isExporting}
             className="w-full flex items-center justify-between bg-slate-700 hover:bg-orange-600 hover:text-white text-slate-200 text-xs px-3 py-2 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
           >
             <div className="flex items-center gap-2">
               {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-orange-400 group-hover:text-white" />}
               <span>PowerPoint (.pptx)</span>
             </div>
             <Download className="w-3 h-3 opacity-50" />
           </button>

           <button 
             onClick={handleExportHighRes}
             disabled={!generatedImage || isExporting}
             className="w-full flex items-center justify-between bg-slate-700 hover:bg-cyan-600 hover:text-white text-slate-200 text-xs px-3 py-2 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
           >
             <div className="flex items-center gap-2">
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4 text-cyan-400 group-hover:text-white" />}
               <span>High-Res TIFF (300 DPI)</span>
             </div>
             <Download className="w-3 h-3 opacity-50" />
           </button>
        </div>

        {/* Annotations List */}
        <div>
          <div className="text-xs text-slate-500 mb-3 font-semibold uppercase flex items-center justify-between">
            <span>Active Labels</span>
            <span className="text-[10px] text-slate-600 bg-slate-800 px-1.5 rounded">{annotations.length}</span>
          </div>
          
          {annotations.length === 0 ? (
            <div className="text-sm text-slate-600 italic text-center py-8 border border-dashed border-slate-800 rounded bg-slate-900/50">
              <MousePointer2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
              Click anywhere on the generated image to add a label.
            </div>
          ) : (
            <div className="space-y-3">
              {annotations.map((ann, idx) => (
                <AnnotationItem 
                  key={ann.id} 
                  annotation={ann}
                  index={idx}
                  onUpdate={updateAnnotationText}
                  onDelete={deleteAnnotation}
                  onFocus={onAnnotationFocus}
                />
              ))}
            </div>
          )}
        </div>

        {/* Prompt Debugger (Useful for Researchers) */}
        <div className="pt-4 border-t border-slate-800">
           <div className="text-xs text-slate-500 mb-2 font-semibold uppercase">Prompt Logic</div>
           <div className="bg-black/30 p-3 rounded text-[10px] text-slate-400 font-mono break-words border border-slate-800 leading-relaxed opacity-70 hover:opacity-100 transition-opacity">
             {generatedPrompt || "// No prompt generated yet..."}
           </div>
        </div>
      </div>
    </div>
  );
};

export default EditorPanel;
